import { http } from "./http";

export type AppointmentType = "TELEMED" | "IN_PERSON" | "FOLLOWUP" | "VACCINE";
export type AppointmentStatus =
  | "WAITING_MED_RECORD"
  | "PENDING"
  | "CONFIRMED"
  | "INPROGRESS"
  | "COMPLETED"
  | "CANCELLED"
  | "NO_SHOW";

export interface AppointmentView {
  id: string;
  hospId: string;
  /** Phase 5.6 — emitted by BE so FE can wire review CTAs for COMPLETED rows. */
  doctorHospId: string | null;
  doctorUsrid: string | null;
  doctorName: string | null;
  departmentId: string | null;
  departmentName: string | null;
  appointmentType: AppointmentType;
  /** ISO-8601 UTC timestamp. */
  scheduledAt: string;
  durationMins: number;
  status: AppointmentStatus;
  complaint: string | null;
  diagnosis: string | null;
  preVitalsBp: string | null;
  preVitalsHr: number | null;
  preVitalsSpO2: number | null;
  preVitalsTemp: string | null;
  cancelReason: string | null;
  cancelledAt: string | null;
  rescheduleReason: string | null;
  rescheduledAt: string | null;
  createdAt: string;
  /** Phase 11.5 — THB amount the coupon saved (only set on book response). */
  couponSaved?: number | null;
  /** Phase 11.5 — non-blocking coupon failure code (COUPON_EXPIRED / etc.). */
  couponError?: string | null;
  /** Phase 12.14 — Promotions package this booking was attributed to (banner
   * + admin reports). NULL when no campaign deep-link was used. */
  linkedCampaign: {
    id: string;
    title: string;
    priceSale: number | null;
  } | null;
  source?: string | null;
  sourceRef?: string | null;
  doctorSpecified?: boolean;
  appointmentCategoryLabel?: string;
  isPackage?: boolean;
  packageCode?: string | null;
  packageName?: string | null;
  packageInfo?: any;
  isOpd?: boolean;
  isTelemed?: boolean;
  rescheduledBy?: string | null;
  cancelledBy?: string | null;
}

export interface BookAppointmentInput {
  hospId: string;
  appointmentType: AppointmentType;
  /** ISO-8601 UTC string — Date.toISOString() produces the right format. */
  scheduledAt: string;
  durationMins?: number;
  complaint?: string;
  preVitalsBp?: string;
  preVitalsHr?: number;
  preVitalsSpO2?: number;
  preVitalsTemp?: string;
  /** Phase 5.1 — explicit doctor selection (omit for auto-picker). */
  doctorHospId?: string;
  doctorUsrid?: string;
  /** Phase 12.16 — explicit department selection. */
  departmentId?: string;
  /** Phase 12.15 — Family Management. ID hash of the dependent if booking for someone else. */
  forPatientIdcardHash?: string;
  /** Phase 11.5 — coupon code carried from promotions page or typed at checkout. */
  couponCode?: string;
  /** Phase 11.5 — cart total in THB the FE displayed. Used for discount math. */
  cartTotal?: number;
  /** Phase 12.13 — Phase 8 vaccine module discriminator. BE creates the
   * VaccineAppointment side-table row when appointmentType=VACCINE. */
  vaccineCode?: string;
  /** Phase 12.13 — Campaign.id when the booking was triggered by a
   * promotions package deep-link. Persisted as Appointment.linkedCampaignId
   * so admin reports can attribute the booking back to a promo. */
  linkedCampaignId?: string;
}

/**
 * Staff-side view of an appointment — includes the customer identifier hash
 * and doctor composite FK so the FE can render the queue + filter by doctor.
 * Medical content is decrypted by the BE (PiiService) before serialisation.
 */
export interface AppointmentStaffView extends AppointmentView {
  idcardHash: string;
  doctorNotes: string | null;
  cancelledBy: string | null;
  rescheduledBy: string | null;
  patientName?: string | null;
  patientGender?: string | null;
  patientAge?: number | null;
}

export interface StaffListFilter {
  hospId?: string;
  status?: string;
  from?: string;
  to?: string;
  doctorUsrid?: string;
  appointmentType?: string;
  doctorSpecified?: string | boolean;
  isPackage?: string | boolean;
  packageCode?: string;
  source?: string;
  page?: number;
  limit?: number;
}

export interface UpdateAppointmentInput {
  complaint?: string;
  doctorNotes?: string;
  diagnosis?: string;
  status?: "INPROGRESS" | "COMPLETED" | "NO_SHOW";
  doctorUsrid?: string;
  scheduledAt?: string;
}

export const appointmentApi = {
  /** Book a new appointment. Doctor is auto-assigned by BE. */
  async book(input: BookAppointmentInput): Promise<AppointmentView> {
    const { data } = await http.post<AppointmentView>(
      "/public/appointment",
      input,
    );
    return data;
  },

  /** Customer's upcoming appointments (PENDING/CONFIRMED/INPROGRESS, scheduledAt >= now). */
  async listUpcoming(): Promise<AppointmentView[]> {
    const { data } = await http.get<AppointmentView[]>(
      "/public/appointment/upcoming",
    );
    return data;
  },

  /**
   * Customer's past appointments (COMPLETED/CANCELLED/NO_SHOW or past scheduledAt).
   * Phase 12.1 — paginated. Defaults to first 50 rows when no opts passed.
   * Returns { items, total, hasMore } so "โหลดเพิ่ม" can decide when to stop.
   */
  async listPast(opts: { limit?: number; offset?: number } = {}): Promise<{
    items: AppointmentView[];
    total: number;
    hasMore: boolean;
  }> {
    const params: Record<string, number> = {};
    if (opts.limit != null) params.limit = opts.limit;
    if (opts.offset != null) params.offset = opts.offset;
    const { data } = await http.get<{
      items: AppointmentView[];
      total: number;
      hasMore: boolean;
    }>("/public/appointment/past", { params });
    return data;
  },

  async get(id: string): Promise<AppointmentView> {
    const { data } = await http.get<AppointmentView>(
      `/public/appointment/${id}`,
    );
    return data;
  },

  /** Cancel own appointment (only PENDING/CONFIRMED). */
  async cancel(id: string, reason?: string): Promise<void> {
    await http.delete(`/public/appointment/${id}`, { data: { reason } });
  },

  /** Phase 6 — reschedule own PENDING/CONFIRMED appointment to a new slot. */
  async reschedule(
    id: string,
    input: {
      scheduledAt: string;
      doctorHospId?: string;
      doctorUsrid?: string;
      reason?: string;
    },
  ): Promise<AppointmentView> {
    const { data } = await http.patch<AppointmentView>(
      `/public/appointment/${id}`,
      input,
    );
    return data;
  },

  /** Phase 6.1 — kiosk check-in payload (decrypted idcard + QR string). */
  async checkin(id: string): Promise<CheckinInfoView> {
    const { data } = await http.get<CheckinInfoView>(
      `/public/appointment/${id}/checkin`,
    );
    return data;
  },
};

export interface CheckinInfoView {
  appointmentId: string;
  hospId: string;
  appointmentType: AppointmentType;
  scheduledAt: string;
  doctorName: string | null;
  idcard: string;
  idcardLast4: string;
  patientName: string | null;
  qrPayload: string;
  packageName?: string | null;
}

/**
 * Staff endpoints — `/api/v1/appointment/*` (no `public/` prefix).
 * Backed by Phase 2 BE; gated by RolesGuard (DOCTOR / NURSE / ADMIN / SUPERADMIN).
 */
export const staffAppointmentApi = {
  async list(filter: StaffListFilter = {}): Promise<AppointmentStaffView[]> {
    const params: Record<string, any> = {};
    if (filter.hospId) params.hospId = filter.hospId;
    if (filter.status) params.status = filter.status;
    if (filter.from) params.from = filter.from;
    if (filter.to) params.to = filter.to;
    if (filter.doctorUsrid) params.doctorUsrid = filter.doctorUsrid;
    if (filter.appointmentType) params.appointmentType = filter.appointmentType;
    if (filter.doctorSpecified !== undefined) params.doctorSpecified = filter.doctorSpecified;
    if (filter.isPackage !== undefined) params.isPackage = filter.isPackage;
    if (filter.packageCode) params.packageCode = filter.packageCode;
    if (filter.source) params.source = filter.source;
    if (filter.page) params.page = String(filter.page);
    if (filter.limit) params.limit = String(filter.limit);
    const { data } = await http.get<AppointmentStaffView[]>("/appointment", {
      params,
    });
    return data;
  },

  async get(id: string): Promise<AppointmentStaffView> {
    const { data } = await http.get<AppointmentStaffView>(`/appointment/${id}`);
    return data;
  },

  async update(
    id: string,
    input: UpdateAppointmentInput,
  ): Promise<AppointmentStaffView> {
    const { data } = await http.patch<AppointmentStaffView>(
      `/appointment/${id}`,
      input,
    );
    return data;
  },

  openRecord(id: string): Promise<AppointmentStaffView> {
    return http.patch(`/appointment/${id}/open-record`).then((res) => res.data);
  },

  getPatient(id: string): Promise<{ idcard: string; idcardHash: string; patientName: string | null; insuranceRight: string }> {
    return http.get(`/appointment/${id}/patient`).then((res) => res.data);
  },

  /** Phase 6 — staff-initiated reschedule. Requires reason. */
  async reschedule(
    id: string,
    input: {
      scheduledAt: string;
      doctorUsrid?: string;
      reason: string;
    },
  ): Promise<AppointmentStaffView> {
    const { data } = await http.patch<AppointmentStaffView>(
      `/appointment/${id}/reschedule`,
      input,
    );
    return data;
  },

  async cancel(id: string, reason: string): Promise<AppointmentStaffView> {
    const { data } = await http.post<AppointmentStaffView>(
      `/appointment/${id}/cancel`,
      { reason },
    );
    return data;
  },

  async getPipeline(params?: any): Promise<any> {
    const { data } = await http.get("/appointment/pipeline", { params });
    return data;
  },
};
